import type { Attachment, AuditLog, FollowUp, Opportunity, Quote, Reminder, Sample, TimelineEvent, TimelineEventKind } from "../types/crm";

export interface TimelineSources {
  followUps: FollowUp[];
  samples: Sample[];
  quotes: Quote[];
  opportunities: Opportunity[];
  reminders: Reminder[];
  auditLogs: AuditLog[];
  attachments: Attachment[];
}

const stageLabels: Record<string, string> = {
  new: "新询盘", contacted: "已联系", qualified: "已确认", sampling: "打样中", quoted: "已报价", negotiating: "谈判中", won: "已成交", lost: "已流失", dormant: "暂缓",
  new_inquiry: "新询盘", new_lead: "新线索", to_contact: "待联系", replied: "已回复", needs_confirmed: "需求确认", high_intent: "高意向", quotation: "报价", negotiation: "谈判", paused: "暂停", invalid: "无效",
};

function actor(name?: string | null) {
  return name?.trim() || "系统";
}

function timelineDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
}

function auditEventKind(action: string): TimelineEventKind {
  if (action.startsWith("sample.")) return "sample";
  if (action.startsWith("quote.")) return "quotation";
  if (action.startsWith("opportunity.")) return "opportunity";
  if (action.includes("stage_change")) return "stage_change";
  if (action.includes("owner_change")) return "owner_change";
  return "system";
}

function auditContent(log: AuditLog) {
  const summary = typeof log.details.summary === "string" ? log.details.summary : null;
  if (summary) return summary;
  const from = typeof log.details.from === "string" ? (stageLabels[log.details.from] || log.details.from) : "未设置";
  const to = typeof log.details.to === "string" ? (stageLabels[log.details.to] || log.details.to) : "未设置";
  return `${from} → ${to}`;
}

export function buildCustomerTimeline(sources: TimelineSources): TimelineEvent[] {
  const followUps: TimelineEvent[] = sources.followUps.map((item) => ({
    id: `follow-up:${item.id}`,
    kind: item.channel === "email" ? "email" : item.channel === "whatsapp" ? "whatsapp" : item.channel === "other" ? "note" : "follow_up",
    occurred_at: item.occurred_at,
    actor_id: item.author_id,
    actor_name: actor(item.author_name),
    channel: item.channel,
    title: item.subject || (item.channel === "email" ? "邮件跟进" : item.channel === "whatsapp" ? "WhatsApp 跟进" : "客户跟进"),
    content: item.content,
    related_type: "follow_up",
    related_id: item.id,
    related_label: item.next_follow_up_at ? `下次跟进：${timelineDate(item.next_follow_up_at)}` : null,
    status: null,
    attachments: [],
  }));

  const samples: TimelineEvent[] = sources.samples.map((item) => ({
    id: `sample:${item.id}`,
    kind: "sample",
    occurred_at: item.updated_at || item.created_at,
    actor_id: item.owner_id,
    actor_name: actor(item.owner_name),
    channel: "sample",
    title: `样品：${item.product_name}`,
    content: item.notes,
    related_type: "sample",
    related_id: item.id,
    related_label: item.tracking_no ? `运单号 ${item.tracking_no}` : item.is_shipped ? "已寄出" : "样品业务",
    status: item.status,
    attachments: [],
  }));

  const quotes: TimelineEvent[] = sources.quotes.map((item) => ({
    id: `quote:${item.id}`,
    kind: "quotation",
    occurred_at: item.updated_at || item.created_at,
    actor_id: item.created_by,
    actor_name: actor(item.creator_name),
    channel: "quote",
    title: `报价 ${item.quote_no}`,
    content: item.notes,
    related_type: "quotation",
    related_id: item.id,
    related_label: `${item.currency} ${Number(item.total_amount).toLocaleString("en-US")}`,
    status: item.status,
    attachments: [],
  }));

  const opportunities: TimelineEvent[] = sources.opportunities.map((item) => ({
    id: `opportunity:${item.id}`,
    kind: "opportunity",
    occurred_at: item.updated_at || item.created_at,
    actor_id: item.owner_id,
    actor_name: actor(item.owner_name),
    channel: null,
    title: `商机：${item.title}`,
    content: item.notes,
    related_type: "opportunity",
    related_id: item.id,
    related_label: item.amount == null ? `成交概率 ${item.probability}%` : `${item.currency} ${Number(item.amount).toLocaleString("en-US")} · 概率 ${item.probability}%`,
    status: item.stage,
    attachments: [],
  }));

  const reminders: TimelineEvent[] = sources.reminders.map((item) => ({
    id: `task:${item.id}`,
    kind: "task",
    occurred_at: item.updated_at || item.created_at,
    actor_id: item.owner_id,
    actor_name: actor(item.owner_name),
    channel: null,
    title: item.title,
    content: item.completed_at ? "待办已完成" : `计划时间：${item.due_at}`,
    related_type: "task",
    related_id: item.id,
    related_label: item.completed_at ? "已完成" : "待处理",
    status: item.completed_at ? "completed" : item.due_status,
    attachments: [],
  }));

  const auditLogs: TimelineEvent[] = sources.auditLogs.map((item) => ({
    id: `audit:${item.id}`,
    kind: auditEventKind(item.action),
    occurred_at: item.created_at,
    actor_id: item.actor_id,
    actor_name: actor(item.actor_name),
    channel: null,
    title: item.action.startsWith("sample.") ? "样品动态" : item.action.startsWith("quote.") ? "报价动态" : item.action.startsWith("opportunity.") ? "商机动态" : item.action.includes("stage_change") ? "销售阶段变更" : item.action.includes("owner_change") ? "负责人变更" : "系统事件",
    content: auditContent(item),
    related_type: "system",
    related_id: item.entity_id,
    related_label: item.entity_type,
    status: null,
    attachments: [],
  }));

  const attachments: TimelineEvent[] = sources.attachments.map((item) => ({
    id: `attachment:${item.id}`,
    kind: "system",
    occurred_at: item.created_at,
    actor_id: item.uploaded_by,
    actor_name: "业务员",
    channel: null,
    title: `上传附件：${item.original_name}`,
    content: `${item.mime_type} · ${(item.size_bytes / 1024).toFixed(1)} KB`,
    related_type: "attachment",
    related_id: item.id,
    related_label: "私有附件",
    status: null,
    attachments: [item],
  }));

  return [...followUps, ...samples, ...quotes, ...opportunities, ...reminders, ...auditLogs, ...attachments]
    .filter((item) => !Number.isNaN(new Date(item.occurred_at).getTime()))
    .sort((left, right) => new Date(right.occurred_at).getTime() - new Date(left.occurred_at).getTime());
}
